// Wording for ensemble insights: the one source for the Insights menu, the
// pointer cards, the side card, the About panel and the tooltip rows
// (ensemble insights spec, section 3). Times are UTC.
import { ENSEMBLE_MODELS } from './ensembleModels';

const MINUS = '−';
const GE = '≥';

export const INSIGHT_TYPES = ['ramp_down', 'ramp_up', 'split', 'revision', 'crowd', 'observed'];

// The two ways ramp pointers are found (menu "Ramps" choice, addendum spec 2).
export const RAMP_METHOD_TEXT = {
  forecast: { name: 'Forecast-based', hint: 'Big moves of the ensemble forecast line' },
  crowd: { name: 'Crowd', hint: 'How many forecasters expect a sharp change' },
};

// Engine defaults (spec section 9, forecast/src/insights/params.py), used
// only until a GET response brings the params in force.
export const INSIGHT_DEFAULT_PARAMS = {
  reference_days: 30, reference_percentile: 95, reference_profile: 'time_of_day', active_floor: 0.3,
  min_reference_days: 7, sharp_threshold: 0.15, ramp_change_h: 1, share_window_min: 30, ramp_flag_share: 0.2, ramp_window_h: 2.5,
  ramp_strong_share: 0.3, ramp_strong_size: 0.15,
  split_min_share: 0.18, split_separation: 4, split_min_gap: 0.1, split_min_duration_h: 1, split_min_group: 2,
  min_forecasters: 10, revision_flag: 0.08, revision_strong: 0.15, strong_cap_per_day: 3,
  reference: 'measurements',
};
export const paramsInForce = (params) => ({ ...INSIGHT_DEFAULT_PARAMS, ...(params || {}) });

export const fmtPct = (v) => `${Math.round((v ?? 0) * 100)}%`;
export const fmtMw = (v) => (v == null ? '-' : (Math.round(v / 10) * 10).toLocaleString('en-US'));
export const fmtSignedMw = (v) => {
  if (v == null) return '-';
  const sign = v > 0 ? '+' : v < 0 ? MINUS : '';
  return `${sign}${fmtMw(Math.abs(v))}`;
};
export const fmtHm = (iso) => (iso ? iso.slice(11, 16) : '');
export const fmtDay = (iso) =>
  new Date(iso).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });
const dayMonth = (iso) => new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });
// "16:00 to 19:00", or "7 Oct 22:30 to 8 Oct 01:30" when the window's last
// quarter-hour falls on another UTC day than its first (the tool's span()).
const windowSpan = (p, sep) => {
  const last = new Date(Date.parse(p.end) - 15 * 60 * 1000).toISOString();
  return p.start.slice(0, 10) === last.slice(0, 10)
    ? `${fmtHm(p.start)}${sep}${fmtHm(p.end)}`
    : `${dayMonth(p.start)} ${fmtHm(p.start)}${sep}${dayMonth(p.end)} ${fmtHm(p.end)}`;
};

const num = (v) => String(Number(Number(v).toFixed(3)));
const hours = (h) => (Number(h) === 1 ? '1 hour' : `${num(h)} hours`);
// "3 h", "1 h 30 min", "30 min": the forecast-based change window, as in the tuning tool's cards.
const hrs = (h) => {
  const min = Math.round(Number(h) * 60);
  if (min < 60) return `${min} min`;
  return `${Math.floor(min / 60)} h${min % 60 ? ` ${min % 60} min` : ''}`;
};
const ordinal = (v) => {
  const n = Math.round(v);
  const teen = n % 100 >= 11 && n % 100 <= 13;
  return `${n}${teen ? 'th' : ({ 1: 'st', 2: 'nd', 3: 'rd' }[n % 10] || 'th')}`;
};
const word = (p) => (p.type === 'ramp_up' ? 'rise' : 'drop');
const isRamp = (p) => p.type === 'ramp_down' || p.type === 'ramp_up';

// Which method found a ramp pointer. Rows stored before the forecast-based
// method have untagged ramps: those are crowd ramps.
export const rampMethodOf = (p, params) => p.method
  || (params?.ramp_method === 'forecast' && p.metrics?.size_mw != null ? 'forecast' : 'crowd');
const isForecastRamp = (p, params) => isRamp(p) && rampMethodOf(p, params) === 'forecast';
// Forecast-based wording: the verb and the MW are the forecast line's own change.
const lineVerb = (m) => (m.size_mw < 0 ? 'drops' : 'rises');
const sunny = (f) => f.shape === 'usual';
// Settings of the forecast-based method in force (params.forecast_ramp);
// before insights load, or for rows computed earlier, there are none.
const fr = (p) => p?.forecast_ramp || {};
const known = (f) => f.min_size != null;
// The engine's line: one ensemble model (params.forecast_ramp.ensemble_model),
// whatever model the dashboard shows.
const modelName = (f) => ENSEMBLE_MODELS.find((x) => x.value === f.ensemble_model)?.label;
const modelLine = (f) => (modelName(f) ? `the ${modelName(f)} ensemble median (whichever model you view)` : 'the ensemble median');
const lineWho = (m, f) => {
  if (m.series === 'crowd_median') return 'Forecasters’ median';
  return modelName(f) ? `${modelName(f)} forecast` : 'Forecast';
};
const minus = (s) => s.replace(/ - /g, ` ${MINUS} `);
// The API keeps at most strong_cap_per_day strong pointers per day and shows
// the rest as weak (spec 3.9), so a pointer can meet the strong rule and be weak.
const capped = (p, strongByRule, params) => (strongByRule && p.strength === 'weak'
  ? [`Shown as weak, because at most ${params.strong_cap_per_day} strong pointers are kept per day.`]
  : []);
const flat = (p) => p.reference_profile === 'flat';
// "within 1 hour", "within 3 hours": the window a forecaster's change is measured over.
const within = (p) => `within ${hours(p.ramp_change_h)}`;
const rampRule = (w, p) => (w === 'drop'
  ? `sharp drop: r(t + ${num(p.ramp_change_h)} h) - r(t) <= ${MINUS}${num(p.sharp_threshold)}`
  : `sharp rise: r(t + ${num(p.ramp_change_h)} h) - r(t) >= +${num(p.sharp_threshold)}`);
// The usual level, as the engine computed it: from measurements, or from the
// forecasters' own q50s when there were too few days of measurements; one level
// per time of day, or one level for the whole day (flat).
const usualLine = (p) => {
  if (p.reference === 'crowd') {
    return flat(p)
      ? `usual = ${ordinal(p.reference_percentile)} percentile of all the forecasters’ q50 values (fewer than ${p.min_reference_days} days of measurements)`
      : `usual(t) = ${ordinal(p.reference_percentile)} percentile of the forecasters’ q50 at t (fewer than ${p.min_reference_days} days of measurements)`;
  }
  return flat(p)
    ? `usual = ${ordinal(p.reference_percentile)} percentile of measured output, last ${p.reference_days} days (one level for the whole day)`
    : `usual(t) = ${ordinal(p.reference_percentile)} percentile of measured output at this time of day, last ${p.reference_days} days`;
};

// About panel, top of the Indicators tab: the two ways ramp pointers are
// found, side by side in operator words, whichever is in use (`inUse`), so the
// choice under Ramps can be made knowingly. Numbers come from the params in
// force; the forecast-based ones (params.forecast_ramp) only once loaded, and
// rows computed before that method have none (`noForecastRamps`).
export const aboutRampMethods = (p, { noForecastRamps = false } = {}) => {
  const f = fr(p);
  const set = known(f);
  const solar = set && sunny(f);
  const line = 'the ensemble forecast'; // one model, the chart's default; not named here
  const as = f.agree_fraction === 0.5 ? 'at least half as big' : `at least ${fmtPct(f.agree_fraction)} as big`;
  let note = null;
  if (!p.forecast_ramp) {
    note = noForecastRamps
      ? 'These days were computed before forecast-based ramps existed, so they have none until they are computed again.'
      : 'Its numbers appear once insights are loaded.';
  }
  let usual = 'the usual level (see Data and timing)';
  if (p.reference !== 'crowd') {
    usual = flat(p)
      ? `the usual level (close to the best output over the last ${p.reference_days} days, one level for the whole day)`
      : `the usual level (close to the best output at that time of day over the last ${p.reference_days} days), so the normal daily shape does not count`;
  }
  return {
    lead: 'Ramp-down and ramp-up risk are found in one of two ways. Choose under Ramps in the Insights menu: the chart and the two ramp sections below follow the choice.',
    methods: [
      {
        key: 'forecast',
        name: RAMP_METHOD_TEXT.forecast.name,
        inUse: p.ramp_method === 'forecast',
        question: `Does ${line} show a big ramp, and do the forecasters show it too?`,
        rows: [
          ['Looks at', `always ${line}, the chart’s default line, even when you view another model.`],
          ['Use it when', 'you plan on the MW the forecast line shows.'],
          ['Flags', set
            ? `the line rising or falling by at least ${fmtPct(f.min_size)} of capacity over ${hrs(f.change_h)}${solar
              ? `, beyond the normal rise and fall of the sun. Only looked for around midday, where a usual sunny day gives at least ${fmtPct(f.active_floor)} of its peak.`
              : '.'}`
            : 'the line rising or falling by a large share of capacity over a few hours.'],
          ['Strong', set
            ? `the line moves at least ${fmtPct(f.strong_size)} of capacity, and at least ${fmtPct(f.strong_share)} of forecasters show a move the same way, ${as}, starting within ${hrs(f.tolerance_h ?? 1)}.`
            : 'a larger move that most forecasters also show in their own forecasts.'],
          ['Size', `in MW, as a share of capacity (the highest measured output in the last ${f.capacity_days ?? 365} days).`],
          ['On the chart', 'the “Expected” row marks the window of each move; its card gives the MW and how many forecasters agree.'],
        ],
        note,
      },
      {
        key: 'crowd',
        name: RAMP_METHOD_TEXT.crowd.name,
        inUse: p.ramp_method !== 'forecast',
        question: `Do at least ${fmtPct(p.ramp_flag_share)} of forecasters each expect a sharp ramp at about the same time, even where the forecast line looks smooth?`,
        rows: [
          ['Looks at', 'each forecaster’s own forecast, one by one. The ensemble is not used.'],
          ['Use it when', 'you want early notice of a ramp that some forecasters see but the forecast line smooths out.'],
          ['Flags', `at least ${fmtPct(p.ramp_flag_share)} of forecasters each expecting a sharp change (${fmtPct(p.sharp_threshold)} of the usual level ${within(p)}), within ${num(p.ramp_window_h)} hours of each other.`],
          ['Strong', `at least ${fmtPct(p.ramp_strong_share)} of forecasters, with a median change of at least ${fmtPct(p.ramp_strong_size)} of the day’s usual peak.`],
          ['Size', `in MW. Changes are judged against ${usual}.`],
          ['On the chart', 'the “Expected” row is shaded at every quarter-hour by the share of forecasters expecting a sharp change there; zoom in for bars inside the chart.'],
        ],
        note: null,
      },
    ],
    differ: [
      `${fmtPct(p.ramp_flag_share)} of forecasters expect a sharp drop and the rest do not, or they all expect it but at different times. ${line[0].toUpperCase()}${line.slice(1)} blends them, so it falls only gently. Crowd flags it; Forecast-based may not.`,
      `Every forecaster expects a slow fall over several hours. None is sharp ${within(p)}, so Crowd stays quiet, while Forecast-based flags it once the line has moved ${set ? `${fmtPct(f.min_size)} of capacity over ${hrs(f.change_h)}` : 'far enough'}.`,
      ...(solar && !flat(p) && f.active_floor > p.active_floor
        ? [`For solar, Crowd also looks at mornings and evenings (where the usual level is at least ${fmtPct(p.active_floor)} of its daily maximum), Forecast-based only around midday (${fmtPct(f.active_floor)}).`]
        : []),
    ],
  };
};

// About panel, last section "Data and timing": who is counted, the usual
// level crowd ramps and splits are measured against, and when each indicator
// is computed (engine step 6 after each session; the API on load).
export const aboutData = (p) => {
  const solarWait = p.reference === 'crowd' && sunny(fr(p))
    ? ` Forecast-based ramps need at least ${p.min_reference_days} days of measurements for solar, so there are none until then.`
    : '';
  let usual;
  if (p.reference === 'crowd') {
    usual = `The usual level, which crowd ramps and splits are measured against, comes here from the forecasters’ own central forecasts (the ${ordinal(p.reference_percentile)} percentile${flat(p) ? ' of all their values' : ' at each quarter-hour'}), because fewer than ${p.min_reference_days} days of measurements were available.${solarWait}`;
  } else {
    usual = `The usual level, which crowd ramps and splits are measured against, is the ${ordinal(p.reference_percentile)} percentile of measured output${flat(p) ? ', one level for the whole day,' : ' at each time of day'} over the last ${p.reference_days} days: close to the best output, leaving out the few highest values.`;
  }
  return [
    `Only forecasters who sent q10, q50 and q90 for every quarter-hour count, and ramps, splits and the crowd at cursor need at least ${p.min_forecasters} of them in a challenge. Crowd ramps, splits, the crowd at cursor and the agreement with forecast-based ramps use their central (q50) forecasts.`,
    `${usual} ${flat(p)
      ? 'Crowd ramps and splits are looked for at every quarter-hour.'
      : `Crowd ramps and splits are only looked for where it is at least ${fmtPct(p.active_floor)} of its daily maximum, which for solar leaves out the night and the edges of the day.`}`,
    'Ramps, splits and the crowd at cursor are computed after each session, and each quarter-hour shows the newest session. Revisions and observed ramps are worked out each time insights load. None of them changes the ensemble. Times are UTC.',
  ];
};

// Menu status when insights cannot be shown (badge "-").
export const INSIGHT_STATUS = {
  too_long: (maxDays) => `Insights cover at most ${maxDays} days. Choose a shorter period to see them.`,
  failed: () => 'Could not load insights.',
};

export const TOOLTIP_LABELS = {
  crowd: (n) => `Crowd (${n})`,
  drop: 'Expect a drop',
  rise: 'Expect a rise',
  rate: 'Ramp rate (crowd)',
  split: 'Two groups',
  observed: (o) => (o.type === 'ramp_up' ? 'Observed rise' : 'Observed drop'),
};

// An observed ramp (addendum spec 7): the measured change and its window.
export const observedValue = (o) => `${fmtMw(Math.abs(o.size_mw))} MW (${fmtPct(Math.abs(o.size_mw) / o.capacity_mw)} of capacity), `
  + `${windowSpan(o, ' to ')} UTC`;

// The Observed strip's card (task 18): the measured change over the rule's
// window, what it is, and whether a shown warning of its direction covered it
// (`covered` null: that ramp indicator is hidden, so nothing was checked).
const measuredWord = (o) => (o.type === 'ramp_up' ? 'rise' : 'drop');
export const observedLabel = (o) => `Measured ${measuredWord(o)}, ${fmtDay(o.start)}, ${windowSpan(o, ' to ')} UTC`;
export const observedCard = (o, rule, covered) => ({
  title: `Measured ${measuredWord(o)}: ${fmtMw(Math.abs(o.size_mw))} MW (${fmtPct(Math.abs(o.size_mw) / o.capacity_mw)} `
    + `of capacity) in ${hrs(rule.steps / 4)}, ${windowSpan(o, ' to ')} UTC`,
  what: 'A big ramp in the measured output, used to check the warnings.',
  verdict: covered == null
    ? `${INSIGHT_DEFS[o.type].name} is hidden, so no warning is checked.`
    : covered ? `A ${measuredWord(o)} warning covered it.` : 'No warning covered it.',
});

// Observed ramps: the rule in force (params.observed_rule), in words. The
// change window is `steps` quarter-hours.
const obsRule = (p) => p?.observed_rule || null;
const obsWithin = (r) => hrs(r.steps / 4);
const observedFormula = (r) => {
  if (!r) return [];
  const h = obsWithin(r);
  const capacity = `capacity = the highest measured output in the ${r.capacity_days} days before the day`;
  if (r.kind !== 'relative') {
    return [`big ramp: |measured(t + ${h}) - measured(t)| >= ${fmtPct(r.threshold)} of capacity`, capacity];
  }
  return [
    `usual(t) = ${ordinal(r.reference_percentile)} percentile of measured output at this time of day, ${r.relative_days} days either side`,
    `change = measured(t + ${h}) / usual(t + ${h}) - measured(t) / usual(t), where usual >= ${fmtPct(r.active_floor)} of its peak`,
    `big ramp: |change| >= ${fmtPct(r.threshold)}`,
    ...(r.min_capacity_share
      ? [r.floor === 'total'
        ? `  and measured(t + ${h}) - measured(t) >= ${fmtPct(r.min_capacity_share)} of capacity, the same way`
        : `  and |change| × usual(t + ${hrs(Math.floor(r.steps / 2) / 4)}) >= ${fmtPct(r.min_capacity_share)} of capacity`]
      : []),
    ...(r.min_output_share
      ? [`  and measured(t + ${h}) - measured(t) >= ${fmtPct(r.min_output_share)} of capacity, the same way`]
      : []),
    capacity,
  ];
};

// About tab "What counts as a ramp": the observed-ramp rule in force in plain
// words, how a warning is judged against it, and that the numbers are
// provisional. Without a rule: none yet for this resource (`loaded`), or
// nothing loaded yet.
export const rampDefinition = (r, { loaded = false } = {}) => {
  if (!r) {
    return [loaded ? 'Ramps are not defined for this resource yet.' : 'What counts as a ramp appears once insights are loaded.'];
  }
  const h = obsWithin(r);
  let rule = `A ramp is when the measured output changes by at least ${fmtPct(r.threshold)} of capacity within ${h}.`;
  const usual = [];
  if (r.kind === 'relative') {
    const parts = [`at least ${fmtPct(r.threshold)} of the usual sunny level`];
    if (r.min_capacity_share) {
      parts.push(r.floor === 'total'
        ? `at least ${fmtPct(r.min_capacity_share)} of capacity`
        : `at least ${fmtPct(r.min_capacity_share)} of capacity once the normal sunrise and sunset are taken out`);
    }
    rule = `A ramp is when, within ${h}, the measured output changes by ${parts.join(' and by ')}`
      + `${r.min_output_share ? `, and the output itself moves at least ${fmtPct(r.min_output_share)} of capacity the same way` : ''}.`;
    usual.push(`The usual sunny level is the ${ordinal(r.reference_percentile)} percentile of measured output at that time of day over the ${r.relative_days} days before and after the day, so it uses hindsight. Only hours where it is at least ${fmtPct(r.active_floor)} of its peak count. It is not the usual level the warnings and splits use: those look only at earlier days.`);
  }
  return [
    'This is the yardstick for what really happened. The warnings use their own rules (Indicators tab) and are checked against this one.',
    rule,
    ...usual,
    `A long ramp counts once, drawn over its steepest ${h}.${r.separation_h ? ` Ramps of one direction are at least ${hrs(r.separation_h)} apart (the larger is kept).` : ''}`,
    `Capacity is the highest measured output in the ${r.capacity_days} days before the day.`,
    'A warning (a strong ramp pointer) is right when a measured ramp of the same direction overlaps its window, and a false alarm when none does. A measured ramp with no warning over it was missed. The menu reads “N observed · k warned”: k counts the measured ramps that a strong ramp pointer of the same direction overlaps, among the ramp indicators ticked in the menu (past days count too). With both ramp indicators ticked, N minus k were missed.',
    'A window that is not fully measured is never called a false alarm. If no ramp has been measured in it yet, it reads “Not measured yet” (it ends after the newest measurement) or “Not measured” (measurements are missing in it).',
    'These thresholds are provisional and will be confirmed with the market owner.',
  ];
};

// `meaning`, `formula` and `thresholds` are functions of the params in force
// wherever they state a number.
const rampDef = (w) => ({
  formula: (p) => [
    usualLine(p),
    'r(t) = forecast q50(t) / usual(t)',
    rampRule(w, p),
    `share(t) = forecasters with a sharp ${w} centred within ${p.share_window_min} min of t / n`,
  ],
  thresholds: (p) => [
    [`Sharp change ${within(p)}`, `${fmtPct(p.sharp_threshold)} of the usual level`],
    ['Flagged', `${fmtPct(p.ramp_flag_share)} of forecasters in the same ${num(p.ramp_window_h)} h window`],
    ['Strong', `${fmtPct(p.ramp_strong_share)} of forecasters and a median ${w} of ${fmtPct(p.ramp_strong_size)} of the day’s usual peak`],
  ],
});

export const INSIGHT_DEFS = {
  ramp_down: {
    name: 'Ramp-down risk',
    line: 'Many forecasters expect a sharp drop at about the same time.',
    meaning: (p) => `Shows when a large share of forecasters expect output to fall sharply ${within(p)}${flat(p) ? '' : ', beyond the normal shape of the day'}. The “Expected” row under the chart shows the share at every quarter-hour; zoom in for bars inside the chart.`,
    not: 'It is not a probability. It counts how many forecasters see the drop.',
    ...rampDef('drop'),
  },
  ramp_up: {
    name: 'Ramp-up risk',
    line: 'Many forecasters expect a sharp rise at about the same time.',
    meaning: 'The same as ramp-down risk, for rises: for example fog clearing, or the wind picking up.',
    not: 'It is not a probability, and it says nothing about how high output gets after the rise.',
    ...rampDef('rise'),
  },
  split: {
    name: 'Split crowd',
    line: 'Forecasters fall into two separate groups, with a gap between them.',
    meaning: (p) => `At each quarter-hour the forecasters’ central forecasts are sorted and divided into the two most consistent groups. When the groups are large and far apart for at least ${hours(p.split_min_duration_h)}, the ensemble median may sit where few forecasters are.`,
    formula: (p) => [
      'split the sorted q50s into lower and upper groups with the least spread within each',
      'separation = (mean upper - mean lower) / pooled standard deviation within groups',
      `split when: smaller group >= ${fmtPct(p.split_min_share)} of n and >= ${p.split_min_group} forecasters, separation >= ${num(p.split_separation)}, gap >= ${fmtPct(p.split_min_gap)} of usual(t)`,
    ],
    thresholds: (p) => [
      ['Smaller group', `at least ${fmtPct(p.split_min_share)} of forecasters, and at least ${p.split_min_group} forecasters`],
      ['Separation', `at least ${num(p.split_separation)} standard deviations`],
      ['Gap', `at least ${fmtPct(p.split_min_gap)} of the usual level`],
      ['Duration', `at least ${hours(p.split_min_duration_h)}; always a strong pointer`],
    ],
    not: 'It does not say which group is right.',
  },
  revision: {
    name: 'Revision',
    line: 'The forecast moved a lot since the previous session.',
    meaning: 'Compares the latest session’s ensemble median with the previous session’s of the same kind (day-ahead with day-ahead, intraday with intraday) for the same quarter-hours, for the model you are viewing. The pointer marks the largest change of the day.',
    formula: (p) => [
      'Δ(t) = q50 latest session(t) - q50 previous session(t)',
      `flag the day when max |Δ(t)| >= ${fmtPct(p.revision_flag)} of the day’s forecast peak`,
    ],
    thresholds: (p) => [
      ['Flagged', `${fmtPct(p.revision_flag)} of the day's forecast peak`],
      ['Strong', `${fmtPct(p.revision_strong)} of the day's forecast peak`],
      ['Shown', 'only with Horizon set to Latest'],
    ],
    not: 'A revision is not an error. It often means new weather information arrived.',
  },
  observed: {
    name: 'Observed ramps',
    line: 'Ramps measured in the output, to check the warnings.',
    meaning: 'Big ramps that actually happened in the measured output: solid blocks on the “Observed” row (ramps '
      + 'measured in the output), under the “Expected” row (ramps expected by the selected method), over a faint '
      + 'track where there are measurements. Hover or click a block for its size and '
      + 'whether a warning covered it. The menu reads “N observed · k warned”: k counts the measured ramps that a '
      + 'strong ramp pointer of the same direction overlaps, among the ramp indicators ticked in the menu (past days '
      + 'count too). With both ramp indicators ticked, N minus k were missed. A ramp pointer’s side card says whether one was '
      + 'measured in its window. Read from the latest measurements each time insights load, so only where there are '
      + 'measurements. The rule, in words, is under “What counts as a ramp”.',
    formula: (p) => observedFormula(obsRule(p)),
    not: 'It is not a forecast: it shows what happened, so the warnings can be checked against it.',
  },
  crowd: {
    name: 'Crowd at cursor',
    line: 'Where the forecasters’ central forecasts sit, on hover.',
    meaning: 'Hover the chart to see the spread of the forecasters’ central forecasts at that quarter-hour, drawn as a violin. Two bulges mean two groups; where the crowd is split, labels give each group’s share. The tooltip’s Crowd row gives the range of the middle 80% of forecasters.',
    formula: ['quantiles 0%, 5%, ..., 100% of the forecasters’ q50 at t', 'violin width ~ 5% / distance between consecutive quantiles'],
    thresholds: (p) => [['Shown', `on hover, at every quarter-hour with at least ${p.min_forecasters} forecasters`]],
    not: 'It is not the forecast uncertainty: that is the grey q10–q90 band. The crowd shows how far forecasters are from each other.',
  },
};

// Forecast-based ramps (addendum spec 3): the same names, other rules.
const forecastRampDef = (w) => ({
  formula: (p) => {
    const f = fr(p);
    if (!known(f)) {
      return [
        'line(t) = the ensemble median (the forecasters’ median where it is missing)',
        'solar: the change of line(t) / usual(t), times the usual level halfway, so the sun’s daily path does not count',
        'wind: the plain change of line(t)',
        `strong: a larger change, and most forecasters show the same ${w}`,
      ];
    }
    const h = f.change_h;
    return [
      `line(t) = ${modelLine(f)}; the forecasters’ median where it is missing`,
      sunny(f)
        ? `Δ(t) = (line(t + ${num(h)} h) / usual(t + ${num(h)} h) - line(t) / usual(t)) × usual(t + ${num(h / 2)} h)`
        : `Δ(t) = line(t + ${num(h)} h) - line(t)`,
      ...(sunny(f)
        ? [`usual(t) = ${ordinal(p.reference_percentile)} percentile of measured output at this time of day, last ${f.reference_days} days`]
        : []),
      `pointer when |Δ| and the line’s own ${w} >= ${fmtPct(f.min_size)} of capacity`,
      `share = forecasters with the same ${w} (at least ${fmtPct(f.agree_fraction)} of it, starting within ${hrs(f.tolerance_h ?? 1)}) / n`,
    ];
  },
  thresholds: (p) => {
    const f = fr(p);
    if (!known(f)) return [];
    return [
      ['Change', `over ${hrs(f.change_h)}, as a share of capacity`],
      ['Capacity', `the highest measured output in the last ${f.capacity_days} days`],
      ...(sunny(f)
        ? [['Usual sunny day', `${ordinal(p.reference_percentile)} percentile of measured output at this time of day, last ${f.reference_days} days; only where at least ${fmtPct(f.active_floor)} of its peak; not the usual level of crowd ramps and splits, which uses ${p.reference_days} days and a ${fmtPct(p.active_floor)} cut-off`]]
        : []),
      ['Flagged', `${fmtPct(f.min_size)} of capacity`],
      ['Strong', `${fmtPct(f.strong_size)} of capacity and ${fmtPct(f.strong_share)} of forecasters showing the same ${w}`],
    ];
  },
  not: 'It is not a probability: it flags big moves of the ensemble forecast.',
});

const FORECAST_DEFS = {
  ramp_down: {
    name: INSIGHT_DEFS.ramp_down.name,
    line: 'The forecast line drops a lot within a few hours.',
    meaning: (p) => `Shows when the ensemble forecast line falls by a large share of capacity over ${known(fr(p)) ? hrs(fr(p).change_h) : 'a few hours'}${sunny(fr(p)) ? ', beyond the normal shape of a sunny day' : ''}, and how many forecasters show the same drop. The “Expected” row under the chart marks these windows: solid for strong pointers, light for weak ones when weak pointers are shown.`,
    ...forecastRampDef('drop'),
  },
  ramp_up: {
    name: INSIGHT_DEFS.ramp_up.name,
    line: 'The forecast line rises a lot within a few hours.',
    meaning: 'The same as ramp-down risk, for rises: for example fog clearing, or the wind picking up.',
    ...forecastRampDef('rise'),
  },
};

// The definition in force: ramps follow the method the response was built with.
export const insightDef = (type, params) => (
  params?.ramp_method === 'forecast' && FORECAST_DEFS[type] ? FORECAST_DEFS[type] : INSIGHT_DEFS[type]);

export const EXTRA_DEFS = [
  {
    key: 'rate',
    name: 'Crowd ramp rate (tooltip)',
    meaning: 'How fast each forecaster expects output to change over the hour around the cursor, summarised across forecasters.',
    formula: ['rate_f(t) = q50_f(t + 30 min) - q50_f(t - 30 min)   [MW/h]', 'shown: median, and 10th to 90th percentile across forecasters'],
    not: 'It is not the ensemble’s own slope, which is usually gentler because forecasters place the ramp at different times.',
  },
  {
    key: 'strength',
    name: 'Strong and weak pointers',
    meaning: (p) => `Each pointer is strong or weak by the rules above. At most ${p.strong_cap_per_day ?? 3} pointers per UTC day stay strong: splits first, then ramps (${p.ramp_method === 'forecast' ? 'larger change in MW' : 'larger share'} first), then revisions (larger change first). The others show as weak. Strong pins are filled, weak pins are rings.`,
    formula: [],
    not: '',
  },
];

export function pointerTitle(p) {
  if (p.type === 'revision') {
    return `Revised ${p.metrics.delta_mw < 0 ? 'down' : 'up'} since the previous session`;
  }
  if (p.type === 'split') return 'Forecasters split into two groups';
  return INSIGHT_DEFS[p.type].name;
}

export function pointerWhen(p) {
  if (p.type === 'revision') return `${fmtDay(p.pin)}, ${fmtHm(p.pin)} UTC`;
  return `${fmtDay(p.start)}, ${fmtHm(p.start)}–${fmtHm(p.end)} UTC`;
}

export function pointerSummary(p, { median, params: raw } = {}) {
  const m = p.metrics;
  const params = paramsInForce(raw);
  if (isForecastRamp(p, params)) {
    const f = fr(params);
    const to = m.to_pct != null && m.to_pct <= 100 ? `, ending at ${Math.round(m.to_pct)}% of a usual sunny day` : '';
    // "Only" where too few agree for a strong pointer.
    const few = known(f) && m.share < f.strong_share ? 'Only ' : '';
    const agree = m.share < 0.8 ? ` ${few}${fmtPct(m.share)} of forecasters show it.` : '';
    return `${lineWho(m, f)} ${lineVerb(m)} ${fmtMw(Math.abs(m.size_mw))} MW in ${hrs(m.change_h)} `
      + `(${windowSpan(p, ' to ')} UTC)${to}.${agree}`;
  }
  if (p.type === 'ramp_down' || p.type === 'ramp_up') {
    return `${m.count} of ${m.n} forecasters (${fmtPct(m.share)}) expect a ${word(p)} of `
      + `${fmtMw(m.size_mw_p10)}–${fmtMw(m.size_mw_p90)} MW ${within(params)}, between ${fmtHm(m.t10)} and ${fmtHm(m.t90)}. `
      + `Steepest crowd rate ${fmtSignedMw(m.steepest_rate_mw_h)} MW/h.`;
  }
  if (p.type === 'split') {
    const between = median != null && median > m.lower && median < m.upper
      ? ` The ensemble median ${fmtMw(median)} MW sits between them, where few forecasters are.`
      : '';
    return `From ${fmtHm(p.start)} to ${fmtHm(p.end)}, ${fmtPct(m.upper_share)} of forecasters expect about `
      + `${fmtMw(m.upper)} MW and ${fmtPct(m.lower_share)} about ${fmtMw(m.lower)} MW (at ${fmtHm(m.at)}).${between}`;
  }
  const energy = m.energy_change_pct == null ? ''
    : ` Day energy ${m.energy_change_pct < 0 ? MINUS : '+'}${fmtPct(Math.abs(m.energy_change_pct))}.`;
  return `At ${fmtHm(m.at)} the median moved from ${fmtMw(m.previous)} to ${fmtMw(m.latest)} MW `
    + `(${fmtSignedMw(m.delta_mw)} MW).${energy}`;
}

// The forecast-based rule with this pointer's numbers. The engine keeps the
// line's own move, not the move beyond the usual sunny day, so for solar a
// weak pointer that passes both checks shown here may be weak for either reason.
// Without the settings (forecast_ramp) no threshold is stated.
function explainForecastRamp(p, params) {
  const m = p.metrics;
  const f = fr(params);
  const set = known(f);
  const w = word(p);
  const cap = m.capacity_mw;
  const move = Math.abs(m.size_mw);
  const minMw = f.min_size * cap;
  const strongMw = f.strong_size * cap;
  const k = Math.round(m.share * m.n);
  // 'mixed' rows fill ensemble gaps with the crowd median, but a window that
  // touches a filled slot is dropped, so its line is the ensemble.
  const line = m.series === 'crowd_median' ? 'the forecasters’ median, because the ensemble was missing' : modelLine(f);
  const passes = move >= strongMw && m.share >= f.strong_share;
  const strong = p.strength === 'strong';
  let verdict;
  if (!set) {
    verdict = strong ? 'Strong.' : 'Weak.';
  } else if (strong) {
    verdict = `Strong, because the ${w} of ${fmtMw(move)} MW ${GE} ${fmtMw(strongMw)} MW and ${fmtPct(m.share)} ${GE} ${fmtPct(f.strong_share)}.`;
  } else if (passes) {
    verdict = sunny(f)
      ? `Weak, either because the ${w} beyond the usual sunny day stays under ${fmtPct(f.strong_size)} of capacity, or because at most ${params.strong_cap_per_day} strong pointers are kept per day.`
      : capped(p, true, params)[0];
  } else {
    verdict = `Weak: strong needs a ${w} of at least ${fmtMw(strongMw)} MW${sunny(f) ? ' (also beyond the usual sunny day)' : ''} and ${fmtPct(f.strong_share)} of forecasters.`;
  }
  return {
    steps: [
      `The line is ${line}. Over ${hrs(m.change_h)} it ${lineVerb(m)} ${fmtMw(move)} MW, from ${windowSpan(p, ' to ')}.`,
      ...(sunny(f)
        ? [`For solar the line is first divided by the usual sunny day (${ordinal(params.reference_percentile)} percentile of measured output at that time of day, last ${f.reference_days} days), so the normal sunrise and sunset do not count; only hours where that level is at least ${fmtPct(f.active_floor)} of its peak.`]
        : []),
      set
        ? `Capacity: the highest measured output in the last ${f.capacity_days} days, ${fmtMw(cap)} MW. A pointer from ${fmtPct(f.min_size)} of capacity (${fmtMw(minMw)} MW), a strong one from ${fmtPct(f.strong_size)} of capacity (${fmtMw(strongMw)} MW).`
        : `Capacity: the highest measured output in the past year, ${fmtMw(cap)} MW.`,
      set
        ? `${fmtPct(m.share)} of forecasters (${k} of ${m.n}) show the same ${w}, each at least ${fmtPct(f.agree_fraction)} of it, starting within ${hrs(f.tolerance_h ?? 1)}; strong needs ${fmtPct(f.strong_share)}.`
        : `${fmtPct(m.share)} of forecasters (${k} of ${m.n}) show the same ${w}.`,
      verdict,
    ],
    formula: [
      `${w} = ${fmtMw(move)} MW / ${fmtMw(cap)} MW = ${fmtPct(move / cap)} of capacity`,
      `share = ${k} / ${m.n} = ${fmtPct(m.share)}`,
    ],
  };
}

// What the measurements say about a ramp pointer's window (`p.measured`, set
// by prepareInsights once observed ramps are loaded); undefined otherwise.
const measuredText = (p) => {
  const v = p.measured;
  if (!v) return undefined;
  if (v.verdict === 'hit') {
    return `A big ${word(p)} was measured in this window: ${fmtMw(Math.abs(v.event.size_mw))} MW, ${windowSpan(v.event, ' to ')} UTC.`;
  }
  if (v.verdict === 'unmeasured') return 'Not measured yet.';
  if (v.verdict === 'gap') return 'Not measured: some measurements are missing in this window.';
  return `No big ramp was measured in this window${p.strength === 'strong' ? ' (false alarm)' : ''}.`;
};

// The side card: `steps` and `formula` of the rule, and for ramps `measured`.
export function explainPointer(p, raw) {
  const out = explainRule(p, raw);
  return out && isRamp(p) ? { ...out, measured: measuredText(p) } : out;
}

function explainRule(p, raw) {
  const params = paramsInForce(raw);
  const m = p.metrics;
  if (isForecastRamp(p, params)) return explainForecastRamp(p, params);
  if (p.type === 'ramp_down' || p.type === 'ramp_up') {
    const w = word(p);
    const strongSize = params.ramp_strong_size * m.reference_peak_mw;
    const strong = m.share >= params.ramp_strong_share && m.size_mw_p50 >= strongSize;
    return {
      steps: [
        flat(params)
          ? 'Each forecaster’s forecast is divided by the usual level, one level for the whole day.'
          : 'Each forecaster’s forecast is divided by the usual level for that time of day, so the normal daily shape does not count.',
        `A sharp ${w} is a change of at least ${fmtPct(params.sharp_threshold)} of the usual level ${within(params)}.`,
        `${m.count} of ${m.n} forecasters place a sharp ${w} between ${fmtHm(m.t10)} and ${fmtHm(m.t90)}: ${m.count} / ${m.n} = ${fmtPct(m.share)}.`,
        `Flagged, because ${fmtPct(m.share)} ${GE} ${fmtPct(params.ramp_flag_share)}.`,
        strong
          ? `Strong, because ${fmtPct(m.share)} ${GE} ${fmtPct(params.ramp_strong_share)} and the median ${w} of ${fmtMw(m.size_mw_p50)} MW ${GE} ${fmtPct(params.ramp_strong_size)} of the usual peak (${fmtMw(strongSize)} MW).`
          : `Weak: strong needs ${fmtPct(params.ramp_strong_share)} of forecasters and a median ${w} of at least ${fmtMw(strongSize)} MW.`,
        ...capped(p, strong, params),
      ],
      formula: [
        'r(t) = q50(t) / usual(t)',
        minus(rampRule(word(p), params)),
        `share = ${m.count} / ${m.n} = ${fmtPct(m.share)}`,
      ],
    };
  }
  if (p.type === 'split') {
    const small = Math.min(m.upper_share, m.lower_share);
    const pooled = (m.upper - m.lower) / m.separation;
    return {
      steps: [
        `At ${fmtHm(m.at)} the forecasts are sorted and divided into the two groups with the least spread inside each.`,
        `Upper group: ${fmtPct(m.upper_share)} around ${fmtMw(m.upper)} MW. Lower group: ${fmtPct(m.lower_share)} around ${fmtMw(m.lower)} MW.`,
        `Counted as split: the smaller group holds ${fmtPct(small)} ${GE} ${fmtPct(params.split_min_share)} and at least ${params.split_min_group} forecasters, the groups are ${m.separation.toFixed(1)} ${GE} ${params.split_separation} standard deviations apart, and the gap of ${fmtMw(m.upper - m.lower)} MW ${GE} ${fmtPct(params.split_min_gap)} of the usual level (${fmtMw(params.split_min_gap * m.reference_mw)} MW).`,
        `It lasts from ${fmtHm(p.start)} to ${fmtHm(p.end)}, at least ${hours(params.split_min_duration_h)}, so it becomes a pointer.`,
        ...capped(p, true, params),
      ],
      formula: [
        `separation = (mean upper ${MINUS} mean lower) / pooled SD`,
        `= (${fmtMw(m.upper)} ${MINUS} ${fmtMw(m.lower)}) / ${fmtMw(pooled)} = ${m.separation.toFixed(1)}`,
      ],
    };
  }
  if (p.type === 'revision') {
    const ratio = m.delta_pct_of_peak;
    return {
      steps: [
        `At ${fmtHm(m.at)}, the latest session’s median is ${fmtMw(m.latest)} MW; the previous session’s was ${fmtMw(m.previous)} MW.`,
        `Change: ${fmtMw(m.latest)} ${MINUS} ${fmtMw(m.previous)} = ${fmtSignedMw(m.delta_mw)} MW, the largest of the day.`,
        `That is ${fmtPct(ratio)} of the day’s forecast peak (${fmtMw(m.day_peak_mw)} MW). Flagged from ${fmtPct(params.revision_flag)}; strong from ${fmtPct(params.revision_strong)}, so this one is ${ratio >= params.revision_strong ? 'strong' : 'weak'}.`,
        ...capped(p, ratio >= params.revision_strong, params),
      ],
      formula: [
        `Δ = ${fmtMw(m.latest)} ${MINUS} ${fmtMw(m.previous)} = ${fmtSignedMw(m.delta_mw)} MW`,
        `|Δ| / peak = ${fmtPct(ratio)}`,
      ],
    };
  }
  return null;
}

// `params`: the payload's params, which say the method of untagged ramps.
export function ribbonLabel(p, detailed, params) {
  const m = p.metrics;
  if (isForecastRamp(p, params)) {
    const head = `${m.size_mw < 0 ? 'Drops' : 'Rises'} ${fmtMw(Math.abs(m.size_mw))} MW`;
    return detailed ? `${head}, ${windowSpan(p, '–')}` : head;
  }
  return detailed
    ? `${fmtPct(m.share)} expect a ${fmtMw(m.size_mw_p50)} MW ${word(p)}, ${fmtHm(m.t10)}–${fmtHm(m.t90)}`
    : `${fmtPct(m.share)} expect a ${word(p)}`;
}

export const peakLabel = (p, params) => (isForecastRamp(p, params)
  ? `${fmtSignedMw(p.metrics.size_mw)} MW in ${hrs(p.metrics.change_h)}`
  : `${fmtPct(p.metrics.share)} · up to ${fmtSignedMw(p.metrics.steepest_rate_mw_h)} MW/h`);
